// Firestore read-side data access for the storefront.
// Queries are intentionally single-field-filtered to avoid composite indexes;
// secondary filtering (status/active) is applied in-memory.
import { db } from "./firebase";
import { collection, doc, getDoc, getDocs, query, where, orderBy } from "firebase/firestore/lite";

const mapDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

// All active products (client-side sort/filter handled by pages).
export async function fetchProducts() {
  const snap = await getDocs(query(collection(db, "products"), where("status", "==", "active")));
  return mapDocs(snap);
}

// Single product by unique slug (status checked in-memory).
export async function fetchProductBySlug(slug) {
  const snap = await getDocs(query(collection(db, "products"), where("slug", "==", slug)));
  const docs = mapDocs(snap).filter((p) => p.status !== "disabled");
  return docs[0] || null;
}

// Active products in one category (by slug).
export async function fetchProductsByCategory(categorySlug) {
  const snap = await getDocs(query(collection(db, "products"), where("category", "==", categorySlug)));
  return mapDocs(snap).filter((p) => p.status !== "disabled");
}

// Categories ordered by displayOrder; disabled ones filtered out in-memory.
export async function fetchCategories() {
  const snap = await getDocs(query(collection(db, "categories"), orderBy("displayOrder")));
  return mapDocs(snap).filter((c) => c.active !== false);
}

export async function fetchCategoryBySlug(slug) {
  const s = await getDoc(doc(db, "categories", slug));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

// Store settings singleton (delivery fee, thresholds, contact, analytics...).
export async function fetchSettings() {
  const s = await getDoc(doc(db, "settings", "store"));
  return s.exists() ? s.data() : null;
}

// Per-page SEO overrides (title/description/keywords), keyed by page key.
// Admin-authored in Admin → SEO; falls back to code defaults when unset.
export async function fetchPageSeo() {
  const s = await getDoc(doc(db, "settings", "seo"));
  return s.exists() ? s.data() : {};
}

// Promo code lookup (doc id == uppercased code).
export async function fetchPromoByCode(code) {
  if (!code) return null;
  const s = await getDoc(doc(db, "promoCodes", String(code).trim().toUpperCase()));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

// All promo codes (used client-side for cart validation; real enforcement is
// server-side in the createOrder Cloud Function in Phase 3).
export async function fetchPromoCodes() {
  const snap = await getDocs(collection(db, "promoCodes"));
  return mapDocs(snap);
}
